

import React, { useRef, useState, useMemo } from "react";
import { ResponsiveBar } from "@nivo/bar";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { followCursor } from 'tippy.js';
import ExportPopover from "./ExportPopover";
import { useChartExport } from "../hooks/useChartExport";
import LeftSidebarIndicators from "./LeftSidebarIndicators";
import PerformanceIndicatorsGrid from "./PerformanceIndicatorsGrid";
import YearSelector from "./YearSelector";
import PerkinsYearSelector from "./PerkinsYearSelector";
import PerkinsViewToggle from "./PerkinsViewToggle";

import PerkinsIndicatorsView from "./PerkinsIndicatorsView";
import { allIndicatorsSubcategory } from "../data/indicatorsData";
import IndicatorsChartPanel from "./IndicatorsChartPanel";

//Realias lengthy names for chart display
function getStudentGroupDisplay(group) {
  const renameMap = {
   // Race/Ethnicity
   'American Indian/ Alaskan Native': 'Am. Indian/AK Native',
   'Native Hawaiian/ Other Pacific Islander': 'Hawaiian/Pac. Isl.',
   'Two or More Races': '2+ Races',
   'Hispanic/ Latino of any race(s)': 'Hisp/Latino',
   'Black/ African American': 'Black/Afr. Am.',
   // Special Populations
   'Economically Disadvantaged': 'Econ. Disadvantaged',
  'Students with Disabilities': 'Students w/ Disab.',
   'Students without Disabilities':'Students w/o Disab.',
   'Non Section 504':               'Non-504',
   'Section 504':                   'Sec. 504',
     // English Learners
  'Non-English Language Learners':  'Non-ELL',
  'English Language Learners':      'ELL',
  };
  if (renameMap[group]) return renameMap[group];
  // Fallback if no alias is defined:
  return group;
}

 function computeYAxisMax({
  indicatorData,
  indicatorsAggData,
  selectedFilterIndicatorSchool,
  selectedDistrictComparison,
  selectedFilterIndicatorGrade,
  indicatorsSubcategory,
  padding = 0.05, // e.g., 5% padding
}) {
  // Coalesce into arrays if null or undefined.
  const safeIndicatorData = Array.isArray(indicatorData) ? indicatorData : [];
  const safeIndicatorsAggData = Array.isArray(indicatorsAggData) ? indicatorsAggData : [];

  let maxSchool = 0;
  let maxComparison = 0;

  // Calculate the maximum value for the main school.
  safeIndicatorData.forEach((row) => {
    if (
      row &&
      row.gradeLevel === selectedFilterIndicatorGrade.value &&
      Array.isArray(row.data)
    ) {
      // Locate the data for the selected school.
      const found = row.data.find(
        (z) => z && z.schoolName === selectedFilterIndicatorSchool.value
      );
      if (found && Array.isArray(found.pct)) {
        // Filter out any null/undefined values.
        const pctValues = found.pct.filter((v) => v != null);
        if (pctValues.length > 0) {
          const localMax = Math.max(...pctValues);
          if (localMax > maxSchool) {
            maxSchool = localMax;
          }
        }
      }
    }
  });

  // Calculate the maximum value for the comparison district (if applicable).
  if (
    selectedDistrictComparison &&
    selectedDistrictComparison.label !== "None" &&
    selectedDistrictComparison.label !== selectedFilterIndicatorSchool.value
  ) {
    safeIndicatorsAggData.forEach((row) => {
      if (
        row &&
        row.gradeLevel === selectedFilterIndicatorGrade.value &&
        row.districtName === selectedDistrictComparison.label &&
        Array.isArray(row.pct)
      ) {
        const pctValues = row.pct.filter((v) => v != null);
        if (pctValues.length > 0) {
          const localMax = Math.max(...pctValues);
          if (localMax > maxComparison) {
            maxComparison = localMax;
          }
        }
      }
    });
  }

  // For target values, use the indicator key from indicatorsSubcategory.
  let targetMax = 0;
  if (
    indicatorsSubcategory &&
    typeof indicatorsSubcategory.value === "string"
  ) {
    // Assume the key is the first three characters.
    const indicatorKey = indicatorsSubcategory.value.slice(0, 3);
    const targetValues = perkinsStateTargets[indicatorKey] || [];
    if (targetValues.length > 0) {
      targetMax = Math.max(...targetValues.filter((v) => v != null));
    }
  }

  // Overall max is the highest value from school, comparison, and target, plus padding.
  const overallMax = Math.max(maxSchool, maxComparison, targetMax) + padding;
  return overallMax;
}




/** Format decimal fraction as e.g. "45.0%" or "–" if null. */
function formatPercent(value) {
  if (value == null) return "–";
  return (value * 100).toFixed(0) + "%";
}

/**
 * define a helper to build the row data from your raw arrays,
 * so we can pass it easily to the bar chart (and also to Excel, etc).
 */
function buildChartData({
  indicatorData,
  indicatorsAggData,
  checkedStudentGroup,
  selectedFilterIndicatorSchool,
  selectedFilterIndicatorGrade,
  selectedYear,
  selectedDistrictComparison,
  stateToggle,
  indicatorsCategory,
  indicatorsSubcategory
}) {
  const isPerkinsCategory = indicatorsCategory?.value === "Perkins V Indicators";
const indicatorKey = isPerkinsCategory
  ? indicatorsSubcategory?.value?.slice(0, 3)
  : null;

const districtLevelOnlyIndicators = ["1S1", "1S2", "2S1"]; // Extend as needed
const isDistrictLevelOnly = indicatorKey && districtLevelOnlyIndicators.includes(indicatorKey);


  const allYears = ["2021", "2022", "2023", "2024", "2025"];
  const yearIndex = allYears.indexOf(selectedYear.value);
  if (yearIndex < 0) return [];

  const dataRows = checkedStudentGroup.map((sg) => {
    // 🔍 Main row finder
const row = indicatorData?.find((r) => {
  return (
    r.studentGroup === sg &&
    (!isPerkinsCategory || r.subcategory === indicatorKey) &&
    (isDistrictLevelOnly || r.gradeLevel === selectedFilterIndicatorGrade.value)
  );
});
    if (!row) return null;

    // Comparison row
    const compRow = indicatorsAggData?.find(
      (agg) =>
        agg.studentGroup === sg &&
        agg.gradeLevel === selectedFilterIndicatorGrade.value &&
        agg.districtName === selectedDistrictComparison.label
    );

    let actualPct = null,
      compPct = null;
    let actualNum = null,
      compNum = null;
    let actualDen = null,
      compDen = null;

    // Main school's row data
    if (row.data && row.data.length) {
      const found = row?.data?.find((z) =>
        isDistrictLevelOnly
          ? z.schoolName === "District Total"
          : z.schoolName === selectedFilterIndicatorSchool.value
      );
      if (found) {
        actualPct = found.pct?.[yearIndex] ?? null;
        actualNum = found.numerator?.[yearIndex] ?? null;
        actualDen = found.denominator?.[yearIndex] ?? null;
      }
    }

    // Comparison row data
    if (compRow?.pct) {
      compPct = compRow.pct?.[yearIndex] ?? null;
      compNum = compRow.numerator?.[yearIndex] ?? null;
      compDen = compRow.denominator?.[yearIndex] ?? null;
    }

    // Determine if the row should be masked.
    // For example, if actualDen is not positive or the percentage is negative.
    const isMasked = (actualDen == null || actualDen <= 0) ||
                     (actualPct != null && actualPct < 0);
    if (isMasked) {
      actualPct = null;
      actualNum = null;
      actualDen = null;
    }

    return {
      studentGroup: sg,
      displayName: getStudentGroupDisplay(sg),
      actualPct,
      actualNum,
      actualDen,
      compPct,
      compNum,
      compDen,
      isScaffold: false, // real row
      isMasked,        // flag if this row is masked
    };
  });

  // Filter out any nulls
  const filtered = dataRows.filter(Boolean);

  // Sort by value (descending) or alphabetically (A-Z for static YoY comparison):
  filtered.sort((a, b) => {
    if (stateToggle) {
      // Alphabetical sort (A-Z) - keeps groups in static position for YoY comparison
      return a.studentGroup.localeCompare(b.studentGroup);
    } else {
      // Sort by value (descending - highest first)
      const valA = a.actualPct || 0;
      const valB = b.actualPct || 0;
      return valB - valA;
    }
  });

  // --- Insert scaffolding rows if we have fewer than 6 groups ---
  const minRows = 8;
  const scaffoldingNeeded = minRows - filtered.length;
  for (let i = 0; i < scaffoldingNeeded; i++) {
    filtered.push({
      studentGroup: `scaffold-${i}`,
      displayName: "", // no label
      actualPct: null, // null so no bar/label is drawn
      actualNum: null,
      actualDen: null,
      compPct: null,
      compNum: null,
      compDen: null,
      isScaffold: true, // scaffold flag
      isMasked: false,  // scaffolds aren't treated as masked data
    });
  }

  return filtered;
}

const perkinsStateTargets = {
  "1S1": [0.836, 0.847, 0.857, 0.868, 0.936],
  "1S2": [0.866, 0.877, 0.887, 0.898, 0.944],
  "2S1": [0.764, 0.787, 0.809, 0.69,  0.616],
  "2S2": [0.571, 0.626, 0.681, 0.394, 0.296],
  "2S3": [0.502, 0.568, 0.635, 0.301, 0.455],
  "3S1": [0.67,  0.702, 0.734, 0.68,  0.771],
  "4S1": [0.518, 0.548, 0.578, 0.21,  0.198],
  "5S1": [0.43,  0.46,  0.49,  0.36,  0.369],
  "5S2": [0.815, 0.829, 0.843, 0.825, 0.968],
  "5S3": [0.9,   0.914, 0.928, 0.51,  0.433],
};

/** 
 * A custom function to produce a tab-delimited string for copy/paste.
 * This is used by `onCopyData` in our export hook. 
 */
function prepareDataForClipboard(chartData, selectedSchool, selectedComp) {
  const lines = [];
  // Headers:
  let headerLine = "Student Group\tSchool %\tSchool Numerator\tSchool Denominator";
  if (selectedComp && selectedComp !== "None" && selectedComp !== selectedSchool) {
    headerLine += `\tComparison %\tComparison Numerator\tComparison Denominator`;
  }
  lines.push(headerLine);

  // Rows:
  chartData.forEach((d) => {
    const { studentGroup, actualPct, actualNum, actualDen, compPct, compNum, compDen } = d;
    let rowLine = `${studentGroup}\t${(actualPct ?? 0).toFixed(3)}\t${actualNum ?? ""}\t${actualDen ?? ""}`;
    if (selectedComp && selectedComp !== "None" && selectedComp !== selectedSchool) {
      rowLine += `\t${(compPct ?? 0).toFixed(3)}\t${compNum ?? ""}\t${compDen ?? ""}`;
    }
    lines.push(rowLine);
  });

  return lines.join("\n");
}

/** 
 * Example of a custom function to populate Excel (optional).
 * If you just want the chart image, skip this or do simpler logic.
 */
async function customExcelTable({ workbook, worksheet, chartData, tsvString }) {
  // 1) Insert some heading
  worksheet.getCell("A1").value = "Performance Indicators Data";

  // 2) Put a small table at row 3
  const headers = ["Student Group", "School Pct", "School Numer", "School Denom", "Comparison Pct", "Comp Numer", "Comp Denom"];
  headers.forEach((hdr, i) => {
    const cell = worksheet.getCell(`${String.fromCharCode(65 + i)}3`);
    cell.value = hdr;
    cell.font = { bold: true };
  });

  // 3) Fill data
  chartData.forEach((row, idx) => {
    const rowNum = 4 + idx;
    worksheet.getCell(`A${rowNum}`).value = row.studentGroup;
    worksheet.getCell(`B${rowNum}`).value = row.actualPct;
    worksheet.getCell(`C${rowNum}`).value = row.actualNum;
    worksheet.getCell(`D${rowNum}`).value = row.actualDen;
    worksheet.getCell(`E${rowNum}`).value = row.compPct;
    worksheet.getCell(`F${rowNum}`).value = row.compNum;
    worksheet.getCell(`G${rowNum}`).value = row.compDen;
  });

  // Format columns B/E as percentages
  worksheet.getColumn("B").numFmt = "0.0%";
  worksheet.getColumn("E").numFmt = "0.0%";
  worksheet.getColumn("A").width = 25;
  ["C","D","F","G"].forEach((col) => (worksheet.getColumn(col).width = 12));
}

const PerformanceIndicatorsBarChart = ({
  viewType,
  indicatorsCategory,
  setViewType,
  selectedYear,
  indicatorsSubcategory,
  indicatorData,
  indicatorsAggData,
  checkedStudentGroup,
  selectedFilterIndicatorSchool,
  selectedDistrictComparison,
  districtName,
  selectedFilterIndicatorGrade,
  stateToggle,
}) => {
  const [alertMessage, setAlertMessage] = useState(null);

  // Quick method to display an alert (or use a toast, etc.)
  const handleAlert = (message, type = "success") => {
    setAlertMessage({ message, type });
    setTimeout(() => setAlertMessage(null), 3000);
  };

  const targetValue = useMemo(() => {
    if (!indicatorsSubcategory || !selectedYear) return null;
    // Use the first three characters of the indicator value as the key.
    const indicatorKey = indicatorsSubcategory.value.slice(0, 3);
    const targetValues = perkinsStateTargets[indicatorKey];
    if (!targetValues || targetValues.length === 0) return null;
    // Map the selected year to the target array index.
    const targetYearMap = { "2021": 0, "2022": 1, "2023": 2, "2024": 3, "2025": 4 };
    const targetIdx = targetYearMap[selectedYear.value];
    if (targetIdx === undefined) return null;
    return targetValues[targetIdx];
  }, [indicatorsSubcategory, selectedYear]);

  const hideTitles = checkedStudentGroup.length > 12;

  
  const indicatorKey = indicatorsSubcategory?.value?.slice(0, 3) ?? null;

  const filteredIndicatorData = useMemo(() => {
    const isPerkins = indicatorsCategory?.value === "Perkins V Indicators";
    const indicatorKey = indicatorsSubcategory?.value?.slice(0, 3);
  
    if (!Array.isArray(indicatorData)) return [];
  
    if (isPerkins && indicatorKey) {
      return indicatorData.filter((d) => d.subcategory === indicatorKey);
    }
  
    // If not Perkins, return all data (don't filter)
    return indicatorData;
  }, [indicatorData, indicatorsCategory?.value, indicatorsSubcategory?.value]);


  // Build chart data
  const chartData = buildChartData({
    indicatorData: filteredIndicatorData, // ✅ filtered before passed in
    indicatorsAggData,
    indicatorsSubcategory,
    checkedStudentGroup,
    selectedFilterIndicatorSchool,
    selectedFilterIndicatorGrade,
    selectedYear,
    selectedDistrictComparison,
    stateToggle,
  });

  // Compute Y-axis max from the actual chart data (ensures it matches what's displayed)
  const computedMax = useMemo(() => {
    if (!chartData.length) return 1; // Default max if no data
    // Get max from actual displayed data
    const maxActual = Math.max(
      ...chartData.map(d => d.actualPct ?? 0).filter(v => v != null && !isNaN(v)),
      0
    );
    // Get max from comparison data
    const maxComp = Math.max(
      ...chartData.map(d => d.compPct ?? 0).filter(v => v != null && !isNaN(v)),
      0
    );
    // Get target value if applicable (Perkins indicators)
    let targetMax = 0;
    if (indicatorsSubcategory?.value) {
      const indicatorKey = indicatorsSubcategory.value.slice(0, 3);
      const targetValues = perkinsStateTargets[indicatorKey] || [];
      if (targetValues.length > 0) {
        targetMax = Math.max(...targetValues.filter(v => v != null));
      }
    }
    // Return overall max with 5% padding
    return Math.max(maxActual, maxComp, targetMax) + 0.05;
  }, [chartData, indicatorsSubcategory]);

  // 2) CALL THE HOOK UNCONDITIONALLY
  const {
    chartRef,
    exportChartAsPNG,
    exportChartToExcel,
    copyImageToClipboard,
    copyDataToClipboard,
  } = useChartExport({
    defaultFileName: "performance_indicators",
    onAlert: handleAlert,
    chartData,
    prepareDataForClipboard: (data) =>
      prepareDataForClipboard(
        data,
        selectedFilterIndicatorSchool.value,
        selectedDistrictComparison.label
      ),
    customExcelLogic: customExcelTable,
  });

  // 3) If there's no data, we can return early
  if (!chartData.length) {
    return <div className="p-4">No data to display.</div>;
  }


  // Our main “keys” array is now just ["actualPct"], because we will NOT
  // show the comparison as a second bar— we’ll do a custom layer for that.
  const keys = ["actualPct"];

 



  const axisBottomTick = (tickProps) => {
    const { value, x, y, textAnchor, tickIndex } = tickProps;
  
    // 1. Skip if there's no value.
    if (!value) return null;
  
    // 2. Skip scaffold rows and masked rows.
    if (typeof value === "string" && (value.startsWith("scaffold-") || value.startsWith("masked-"))) {
      return null;
    }
  
    // 3. Check if the value exists in the new data; if not, skip.
    const inNewData = chartData.some((d) => d.studentGroup === value);
    if (!inNewData) return null;
  
    // 4. Original logic for wrapping or angling text.
    const barCount = chartData.length;
    if (barCount > 12) {
      // Angled text for many bars
      const renamedValue = getStudentGroupDisplay(value);
      return (
        <g key={`axisTick-${tickIndex}-${value}`} transform={`translate(${x}, ${y + 5}) rotate(-45)`}>
          <text x={0} y={0} textAnchor="end" style={{ fill: "#333", fontSize: "0.75rem" }}>
            {renamedValue}
          </text>
        </g>
      );
    } else {
      // Multi-line wrapped text for fewer bars.
      const renamedValue = getStudentGroupDisplay(value);
      const lines = splitTextOnWords(renamedValue, 12);
      return (
        <g key={`axisTick-${tickIndex}-${value}`} transform={`translate(${x}, ${y + 20})`}>
          {lines.map((line, i) => (
            <text
              key={i}
              x={0}
              y={i * 14}
              textAnchor={textAnchor}
              style={{ fill: "#333", fontSize: "0.75rem", fontWeight: "500", fontFamily: "sans-serif, Helvetica Neue, Helvetica, Arial", pointerEvents: "none" }}
            >
              {line}
            </text>
          ))}
        </g>
      );
    };
  };

  const maskedLayer = ({ bars, yScale }) => {
    const baseline = yScale(0);
  
    // Compute the minimum height among the "real" (non-masked) bars.
    const nonMaskedBars = bars.filter(
      (bar) =>
        !bar.data.data.isScaffold &&
        !bar.data.data.isMasked &&
        bar.height > 0
    );
    const minHeight = nonMaskedBars.length
      ? Math.min(...nonMaskedBars.map((bar) => bar.height))
      : 10; // fallback if no real bar exists
  
    return (
      <g>
        {bars.map((bar) => {
          const { isMasked, isScaffold } = bar.data.data;
          // Only draw the overlay for masked (and non-scaffold) rows.
          if (!isMasked) return null;
  
          return (
            <g key={bar.key}>
              <rect
                x={bar.x}
                y={baseline - minHeight} // position above baseline
                width={bar.width}
                height={minHeight}
                stroke="#d3d3d3"     
                strokeWidth={1}
                fill="url(#maskDiagonal)"
              />
              <text
                x={bar.x + bar.width / 2}
                y={baseline - minHeight - 12}
                textAnchor="middle"
                alignmentBaseline="middle"
                style={{ fill: "#d3d3d3", fontStyle: "italic", fontSize: "12px"}}
              >
                Masked
              </text>
            </g>
          );
        })}
        <defs>
          <pattern
            id="maskDiagonal"
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
            patternTransform="rotate(45)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="6"
              stroke="#e4e4e7"
              strokeWidth="1.5"
            />
          </pattern>
        </defs>
      </g>
    );
  };

  // 2) A custom layer that draws the comparison as a dashed line with a small rect,
  //    similar to your `comparisonLayer` example from ClusterParticipationChart.
  const comparisonLayer = ({ bars, yScale }) => {
    // If the comparison is "None" or the same as the school, skip
    if (
      !selectedDistrictComparison ||
      selectedDistrictComparison.label === "None" ||
      selectedDistrictComparison.label === selectedFilterIndicatorSchool.value
    ) {
      return null;
    }

    return (
      <g>
        {bars.map((bar) => {
          // Each “bar” is the “actualPct” bar.
          const { studentGroup, compPct, compNum, compDen } = bar.data.data;
          if (compPct == null) return null; // skip if no comparison

          // Y-coord for the comparison
          const comparisonY = yScale(compPct);
          const rectHeight = 18;
          const offsetAboveLine = -1;

          return (
            <Tippy
              key={`compLine-${studentGroup}`}
              theme="clusterChartBlue"
              content={
                <div
                  className="rounded-sm shadow-md p-2 text-sm text-gray-800 max-w-60 bg-white"
                  style={{ border: `1px solid #475569` }}
                >
                  <div className="font-semibold mb-1 text-gray-700">
                    {studentGroup} – {selectedDistrictComparison.label}
                  </div>
                  <div>
                    {formatPercent(compPct)} ({compNum?.toLocaleString()} of{" "}
                    {compDen?.toLocaleString()})
                  </div>
                </div>
              }
              interactive={false}
              arrow={false}
              followCursor={true}
              placement="bottom-start"
              offset={[5, 5]}
              appendTo={document.body}
              plugins={[followCursor]}
              delay={[100, 0]}
              popperOptions={{
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      padding: 10,
                    },
                  },
                  {
                    name: 'flip',
                    options: {
                      // fallback placements if the tooltip would be clipped
                      fallbackPlacements: ['bottom-end', 'right', 'left']
                    },
                  },
                ],
              }}
            >
              <g>
                {/* Dashed line */}
                <line
                  x1={bar.x - 10}
                  x2={bar.x + bar.width + 10}
                  y1={comparisonY}
                  y2={comparisonY}
                  stroke="#475569"
                  strokeWidth={2}
                  strokeDasharray="6,4"
                />
                {/* Small rect above the line */}
                <rect
                 x={bar.x + bar.width - 20}
                  y={comparisonY - rectHeight - offsetAboveLine}
                  width={29}
                  height={rectHeight}
                  fill="#475569"
                />
                {/* The text inside the rect */}
                <text
                 x={bar.x + bar.width - 5}
                  y={comparisonY - offsetAboveLine - 6}
                 textAnchor="middle"
                 cursor="pointer"
                  style={{
                    fill: "#FFF",
                    fontSize: "0.65rem",
                    fontFamily: "sans-serif, Helvetica Neue, Helvetica, Arial",
                    fontWeight: "bold"
                  }}
                >
                  {formatPercent(compPct)}
                </text>
              </g>
            </Tippy>
          );
        })}
      </g>
    );
  };

  // 3) A custom layer that places the bar’s numeric label above the bar
  const valueLabelLayer = ({ bars }) => {
    return (
      <g>
        {bars.map((bar) => {
          if (bar.data.value == null) return null;
          const labelText = formatPercent(bar.data.value);

          // We'll place a small white rect behind the text for clarity
          // and then the text centered above the bar.
          return (
            <g key={bar.key}>
              {/* White background behind text */}
              <rect
                x={bar.x + bar.width / 2 - 18}
                y={bar.y - 20}
                width={36}
                height={16}
                fill="#fff"
              />
              <text
                x={bar.x + bar.width / 2}
                y={bar.y  - 8}
                textAnchor="middle"
                style={{
                  fill: "#333",
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  fontFamily: "sans-serif, Helvetica Neue, Helvetica, Arial",
                  pointerEvents: "none",
                }}
              >
                {labelText}
              </text>
            </g>
          );
        })}
      </g>
    );
  };

  // 4) A custom “hover” layer for the bars
  const tooltipLayer = ({ bars }) => {
    return (
      <g>
        {bars.map((bar) => {
          const { studentGroup, actualNum, actualDen } = bar.data.data;
          const { value } = bar.data;
          if (value == null) return null;

          return (
            <Tippy
              key={bar.key}
                theme="clusterChartBlue"
              
              content={
                <div
                  className="bg-white rounded-sm shadow-md p-2 text-sm text-gray-800 max-w-60"
                  style={{ border: `1px solid #A3CAFA` }}
                >
                  <div className="font-semibold mb-1">
                    {studentGroup} –{" "}
                    {selectedFilterIndicatorSchool.value === "District Total"
                      ? districtName
                      : selectedFilterIndicatorSchool.value}
                  </div>
                  <div>
                    {formatPercent(value)} ({actualNum?.toLocaleString()} /{" "}
                    {actualDen?.toLocaleString()})
                  </div>
                </div>
              }
              interactive={false}
              arrow={false}
              followCursor={true}
              placement="bottom-start"
              offset={[5, 5]}
              appendTo={document.body}
              plugins={[followCursor]}
              delay={[100, 0]}
              popperOptions={{
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      padding: 10,
                    },
                  },
                  {
                    name: 'flip',
                    options: {
                      // fallback placements if the tooltip would be clipped
                      fallbackPlacements: ['bottom-end', 'right', 'left']
                    },
                  },
                ],
              }}
            >
              <rect
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill="transparent"
              />
            </Tippy>
          );
        })}
      </g>
    );
  };


  // Helper: Splits text into multiple lines respecting word boundaries.
const splitTextOnWords = (text, maxLength) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    if ((currentLine + (currentLine ? ' ' : '') + word).length > maxLength) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine += (currentLine ? ' ' : '') + word;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
};






// perkinsStateTargets, indicatorsSubcategory, selectedYear, and formatPercent.

 // Example: using targetValue within your targetLineLayer:
 const targetLineLayer = ({ yScale, width }) => {
  if (targetValue == null) return null;
  const y = yScale(targetValue);
  return (
    <g>
      <line
        x1={0}
        x2={width -90}
        y1={y}
        y2={y}
        stroke="#475569"
        strokeWidth={1.5}
      />
      <g transform={`translate(${width - 100}, ${y - 5})`}>
        <rect
          x={-76}
          y={-14}
          width={100}
          height={18}
          fill="#fff"
        />
        <text
          textAnchor="end"
          style={{
            fontSize: "0.7rem",
            fill: "#475569",
            fontWeight: "bold",
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
          }}
        >
          WA <tspan style={{ fontSize: "1.2rem" }}>🎯</tspan> {formatPercent(targetValue)}
        </text>
      </g>
    </g>
  );
};




const customTitleLayer = ({ width }) => {
  // Return null if no indicator is selected.

  const dataSource = indicatorsSubcategory || indicatorsCategory;
// If there's truly no data source, bail out
if (!dataSource) return null;
  // Define the texts based on your new mapping.
  const titleText = dataSource.value ?? dataSource.title ?? dataSource.label;
  const subTitle = dataSource.title ??  dataSource.subtitle ?? ""; // or dataSource.title, up to you

  // Calculate max characters per line based on available width.
  const maxCharsPerLine = Math.floor(width / 12);
  // Wrap both the main title and subtitle.
  const wrappedMainTitle = splitTextOnWords(titleText, maxCharsPerLine);
  const wrappedSubTitle = splitTextOnWords(subTitle, maxCharsPerLine);

  // Set font sizes (adjust if needed).
  const mainFontSize = wrappedMainTitle.length > 2 ? "1.05rem" : "1.25rem";
  const subFontSize = wrappedMainTitle.length > 2 ? "0.68rem" : ".89rem";

  // Define line heights in pixels (adjust as needed).
  const lineHeightMain = 20;
  const lineHeightSub = 16;
  // Start main title at a fixed Y position.
  const mainTitleStartY = -100;
  // Position the subtitle below the main title.
  const subTitleStartY = mainTitleStartY + wrappedMainTitle.length * lineHeightMain + 10;

  // Compute the background rectangle's position and height.
  const padding = 10;
  const backgroundY = mainTitleStartY - padding;
  const backgroundHeight =
    wrappedMainTitle.length * lineHeightMain +
    wrappedSubTitle.length * lineHeightSub +
    2 * padding;

  return (
    <g>
      {/* White background covering both title and subtitle */}
      <rect
        x={-100}
        y={backgroundY}
        width={width + 100}
        height={backgroundHeight}
        fill="#ffffff"
      />

      {/* Main title (wrapped into multiple lines) */}
      <text
        x={width / 2 - 100}
        y={mainTitleStartY}
        textAnchor="middle"
        style={{
          fontSize: mainFontSize,
          fontWeight: "bold",
          fill: "#333333",
          fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        }}
      >
        {wrappedMainTitle.map((line, i) => (
          <tspan key={i} x={width / 2 - 100} dy={i === 0 ? 0 : lineHeightMain}>
            {line}
          </tspan>
        ))}
      </text>

      {/* Subtitle (also wrapped) */}
      <text
        x={width / 2 - 100}
        y={subTitleStartY}
        textAnchor="middle"
        style={{
          fontSize: subFontSize,
          fontWeight: "normal",
          fill: "#555555",
          fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        }}
      >
        {wrappedSubTitle.map((line, i) => (
          <tspan key={i} x={width / 2 - 100} dy={i === 0 ? 0 : lineHeightSub}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const missingLayer = ({ bars, yScale }) => {
  // Only show the missing layer if targetValue exists.
  if ( targetValue == null) return null;

  return (
    <g>
      {bars
        .filter((bar) => {
          const data = bar.data.data;
          return (
            bar.data.id === "actualPct" &&
            data.actualPct != null &&
            data.actualDen != null &&
            // Show missing gap only if actual is below target.
            data.actualPct < targetValue
          );
        })
        .map((bar) => {
          const data = bar.data.data;
          const actualPct = data.actualPct;
          const actualDen = data.actualDen;
          if (actualPct >= targetValue) return null;
          const missingFraction = targetValue - actualPct;
          const missingStudents = Math.round(missingFraction * actualDen);
          if (missingStudents <= 0) return null;
          const yActual = yScale(actualPct);
          const yTarget = yScale(targetValue);
          const missingHeight = yActual - yTarget;
          if (missingHeight <= 0) return null;
          const labelWidth = 50;
          const labelHeight = 16;
          const iconSize = 10;
          const spacing = 4;
          const yLabel = yTarget - 22;
          const xCenter = bar.x + bar.width / 2;
          const bigRectHeight = yActual - yLabel;
          if (bigRectHeight <= 0) return null;

          const tooltipContent = (
            <div
              className="bg-white rounded-sm shadow-md p-2 text-sm text-gray-800 border-red-200 border"
            >
              <div className="font-semibold mb-1 w-full text-red-400">Achievement Gap</div>
              <div>
                <strong>Actual:</strong> {(actualPct * 100).toFixed(1)}% vs.{" "}
                <strong>Target:</strong> {(targetValue * 100).toFixed(1)}%
              </div>
              <div className="mt-1">
                <strong>{missingStudents}</strong> more {getStudentGroupDisplay(data.studentGroup)} needed to reach the state target.
              </div>
            </div>
          );

          return (
            <Tippy
              key={`missing-${bar.key}`}
              theme="missingStudents"
              content={tooltipContent}
              placement="bottom-start"
              offset={[5, 5]}
              arrow={false}
              interactive={false}
              appendTo={document.body}
              plugins={[followCursor]}
              followCursor={true}
              delay={[100, 0]}
              popperOptions={{
                modifiers: [
                  { name: 'preventOverflow', options: { padding: 10 } },
                  { name: 'flip', options: { fallbackPlacements: ['bottom-end', 'right', 'left'] } },
                ],
              }}
            >
              <g className="cursor-pointer">
                <rect
                  x={bar.x}
                  y={yLabel}
                  width={bar.width}
                  height={bigRectHeight}
                  fill="transparent"
                />
                <rect
                  x={bar.x}
                  y={yTarget}
                  width={bar.width}
                  height={missingHeight}
                  fill="none"
                  stroke="rgba(255,0,0,0.6)"
                  strokeDasharray="5 4"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
                <rect
                  x={xCenter - labelWidth / 2}
                  y={yLabel}
                  width={labelWidth}
                  height={labelHeight}
                  fill="#ffffff"
                />
                <g
                  transform={`translate(${xCenter - (iconSize + spacing + 20) / 2}, ${yLabel + labelHeight / 2})`}
                >
                  <g transform="translate(0, -5) scale(0.5)">
                    <path
                      fill="rgba(255,0,0,0.7)"
                      d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z"
                    />
                  </g>
                  <text
                    x={iconSize + spacing}
                    y={3}
                    textAnchor="start"
                    style={{
                      fill: "rgba(255,0,0,0.7)",
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                      fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                    }}
                  >
                    x {missingStudents}
                  </text>
                </g>
              </g>
            </Tippy>
          );
        })}
    </g>
  );
};

  // 5) Decide on the chart dimensions / margins
  const chartWidth = 1000;
  const chartHeight = 450;

  // 6) Render
  return (
    <div className="p-8  bg-white rounded-lg shadow-md border border-zinc-200">
    {/* Title Row with controls properly aligned */}
        
        {/* Controls moved to header, properly aligned */}
        <div className="flex space-x-3 items-center justify-between w-full rounded-t-lg p-4  bg-white">
        <div className="text-xl font-semibold">Performance Indicators</div>
        <PerkinsViewToggle />
<div className={viewType !== "bar" ? "invisible" : ""}>
  {indicatorsCategory.value === "Perkins V Indicators" ? (
    <PerkinsYearSelector />
  ) : (
    <YearSelector />
  )}
</div>
          <ExportPopover
            onCopyImage={copyImageToClipboard}
            onCopyData={copyDataToClipboard}
            onExportPNG={exportChartAsPNG}
            onExportExcel={exportChartToExcel}
          />
        </div>
      <hr className="border-zinc-200 border-dotted mx-4 mb-4" />

    {/* Main layout: Sidebar + Main Content */}
    <div className="flex">
      {/* Sidebar */}
      <div className="w-1/5 pr-4">
        <LeftSidebarIndicators />
      </div>

      {/* Main Content */}
      <div className="w-4/5 flex flex-col space-y-4 mt-4">
        {/* Filters + Export Buttons */}
        <div className="flex items-center justify-between">
      <div></div>
     
        </div>
  
          {/* Main Panel: Chart or Grid */}
          {viewType === "bar" ? (
            <div className="flex flex-col space-y-6">
              {/* Chart */}
              <div ref={chartRef} style={{ width: "100%", height: "525px" }}>
                <ResponsiveBar
                  data={chartData}
                  isInteractive={false}
                  labelSkipWidth={12}
                  labelSkipHeight={12}
                  labelTextColor="#000000"
                  // label={d => formatPercent(d.value)}
                  label={false}
                  keys={["actualPct"]}
                  indexBy="studentGroup"
                  margin={{ top: 160, right: 80, bottom: 100, left: 80 }}
                  padding={0.35}
                  minValue={0}
                  maxValue={computedMax}
                  layout="vertical"
                  colors={["#7dd3fc"]}
                  borderWidth={1}
                  borderRadius={1}
                  borderColor={{
                    from: 'color',
                    modifiers: [['darker', '0.55']],
                  }}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    renderTick: axisBottomTick,
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    // legend: 'Percentage (%)',
                    legendPosition: 'middle',
                    legendOffset: -60,
                    tickValues: 5,
                    format: value => `${value * 100}%`,
                  }}
                  theme={{
                    fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                    background: "#FFF",
                    grid: {
                      line: {
                        stroke: '#a3a3a3',
                        strokeWidth: 1,
                        strokeDasharray: '6,4',
                      },
                    },
                    axis: {
                      domain: { line: { stroke: '#777777', strokeWidth: 1 } },
                      ticks: {
                        line: { stroke: '#777777', strokeWidth: 1 },
                        text: {
                          fill: '#333333',
                          fontSize: '0.875rem',
                          fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                          
                        },
                      },
                      legend: {
                        text: {
                          fill: '#333333',
                          fontSize: '1rem',
                          fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                          
                        },
                      },
                    },
                    legends: {
                      text: {
                        fill: '#333333',
                        fontSize: '0.875rem',
                        fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                      },
                    },
                  }}
                  legends={[]}
                  layers={[
                    ({ yScale, width }) => {
                      const ticks = yScale.ticks ? yScale.ticks(5) : yScale.domain();
                      return (
                        <g>
                          {ticks.map((tick, i) => (
                            <line
                              key={i}
                              x1={0}
                              x2={width -150}
                              y1={yScale(tick)}
                              y2={yScale(tick)}
                              stroke="#a3a3a3"
                              strokeDasharray="6,4"
                              strokeWidth={0.25}
                            />
                          ))}
                        </g>
                      );
                    },
                    "bars",
           
                  valueLabelLayer,
                    tooltipLayer,
                    customTitleLayer,
                    maskedLayer,
                    comparisonLayer,
                    targetLineLayer,           
                    // missingLayer,
                    "legends",
                    "axes",
                  ]}
                />
              </div>
  
              {/* Data Table */}
              <div className="-ml-[20%]">
    {/* <PerkinsMeasureTable 
      chartData={chartData}
      selectedSchool={selectedFilterIndicatorSchool}
      selectedComparison={selectedDistrictComparison}
      districtName={districtName}
    /> */}
  </div>
            </div>
          ) : (
            // Grid View
            <PerformanceIndicatorsGrid
              indicatorData={indicatorData}
              indicatorsAggData={indicatorsAggData}
              checkedStudentGroup={checkedStudentGroup}
              selectedFilterIndicatorSchool={selectedFilterIndicatorSchool}
              selectedDistrictComparison={selectedDistrictComparison}
              districtName={districtName}
              selectedFilterIndicatorGrade={selectedFilterIndicatorGrade}
              selectedYear={selectedYear}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceIndicatorsBarChart;